import { useCallback } from "react";
import { useNavigate, useOutletContext } from "react-router";

import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import MetaInfoProvider from "components/MetaInfoList/Provider";
import PaginationIndicator from "components/PaginationIndicator";
import SearchInput from "components/SearchInput";
import SpacePath from "components/SpacePath";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeaderTitle from "components/ViewHeader/Title";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import Badge from "ds/components/Badge";
import Button from "ds/components/Button";
import PageInfoHeader from "ds/components/PageInfoHeader";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useCanManageVCSIntegration from "shared/VCS/access/useCanManageVCSIntegration";
import { VcsProvider } from "types/generated";
import { useLayoutActions } from "views/Account/Integrations/hooks/useLayoutActions";
import { useLayoutHeading } from "views/Account/Integrations/hooks/useLayoutHeading";
import { useLayoutMeta } from "views/Account/Integrations/hooks/useLayoutMeta";
import { IntegrationsOutletContext } from "views/Account/Integrations/types";

import { showVCSIntegrationDetailsDrawer } from "../VcsShared/components/Details";
import VCSIntegrationFeedback from "../VcsShared/components/IntegrationFeedback";
import { getEditIntegrationURL } from "../VcsShared/helpers";
import VCSIntegrationDropdownActions from "./DropdownActions";
import VCSIntegrationEmpty from "./Empty";
import VCS_INTEGRATION_USED_BY_TABLE_CELLS from "./Table/Cells";
import { SORT_CONFIG, VCS_INTEGRATION_USED_BY_TABLE_FILTERS_STORAGE_KEY } from "./Table/constants";
import useVCSIntegrationUsedByTableStore from "./Table/useTableStore";
import { useSearchVCSIntegrationUsedBy } from "./useSearchVCSIntegrationUsedBy";

const VCSIntegration = () => {
  const navigate = useNavigate();
  const { integration: integrationDetails } = useOutletContext<IntegrationsOutletContext>();
  const trackAnalytics = useAnalytics();

  const {
    integration,
    usedByItems,
    isPageLoading,
    isPageEmpty,
    isPageRefetching,
    totalEntitiesCount,
    error,
    hasNextPage,
    loadMoreItems,
  } = useSearchVCSIntegrationUsedBy();
  useTitle(integration?.name ? `Integrations · ${integration.name}` : "Integrations");

  const handleOpenDetailsDrawer = () => {
    showVCSIntegrationDetailsDrawer({
      item: integration,
      canEdit: canManageVCSIntegrations,
    });
  };

  const handleIntegrationEdit = useCallback(() => {
    if (integration?.id) {
      navigate(getEditIntegrationURL(integration.provider, integration.id));
    }
  }, [navigate, integration]);

  // FYI: incorrect type as integration.space can be undefined in certain circumstances
  const canManageVCSIntegrations = useCanManageVCSIntegration()(integration);

  const handleIntegrationDelete = useCallback(() => {
    navigate(`/integrations/${integrationDetails?.id}`);
  }, [navigate, integrationDetails]);

  useLayoutHeading(
    <>
      <ViewHeaderTitle>{integration?.name}</ViewHeaderTitle>
      {integration?.isDefault && <Badge variant="gray" type="strong" text="DEFAULT" />}
    </>,
    integration?.name ?? ""
  );
  useLayoutMeta(
    <>
      <SpacePath
        variant="meta"
        spaceId={integration?.space?.id}
        fallbackName={integration?.space?.name}
      />
      <MetaInfoProvider provider={integration?.provider as VcsProvider} />
      <TimestampMetaInfo timestamp={integration?.updatedAt ?? 0} />
    </>
  );
  useLayoutActions(
    <>
      <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
        Details
      </Button>

      {canManageVCSIntegrations && integration && (
        <VCSIntegrationDropdownActions
          integration={integration}
          onEdit={handleIntegrationEdit}
          totalEntitiesCount={totalEntitiesCount}
          onDelete={handleIntegrationDelete}
        />
      )}
    </>
  );

  useBreadcrumbs(
    [
      {
        title: "Integrations",
        link: "/integrations",
      },
      {
        title: integrationDetails?.name || "",
        link: `/integrations/${integrationDetails?.id}`,
      },
      {
        title: integration?.name || "",
      },
    ],
    [integration?.name]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (isPageLoading) {
    return <PageLoading />;
  }

  if (!integration) {
    return <NotFoundPage />;
  }

  return (
    <>
      <VCSIntegrationFeedback integration={integration} viewPageMode />

      <PageInfoHeader title="Used by">
        {!isPageEmpty && (
          <>
            <PaginationIndicator
              currentCount={usedByItems.length}
              totalCount={totalEntitiesCount}
              loading={isPageRefetching}
              minimumLoadingDuration={200}
            />

            <SearchInput
              placeholder={SEARCH_PLACEHOLDERS.VCS_USED_BY}
              filtersOrderSettingsKey={VCS_INTEGRATION_USED_BY_TABLE_FILTERS_STORAGE_KEY}
            />
          </>
        )}
      </PageInfoHeader>

      <TableSimple
        data={usedByItems}
        itemIdKey="stackId"
        loading={isPageLoading}
        store={useVCSIntegrationUsedByTableStore}
        ariaLabel={`${integration.name} used by`}
        onLoadMore={hasNextPage ? loadMoreItems : undefined}
        trackAnalytics={trackAnalytics}
        cellPerColumn={VCS_INTEGRATION_USED_BY_TABLE_CELLS}
        filtersStorageKey={VCS_INTEGRATION_USED_BY_TABLE_FILTERS_STORAGE_KEY}
        sortConfig={SORT_CONFIG}
        emptyState={<VCSIntegrationEmpty />}
        virtualize={usedByItems.length >= 50}
      />
    </>
  );
};

export default VCSIntegration;
